"use client"

import { useState, useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Rnd } from "react-rnd"
import { styled } from "@mui/material/styles"
import fetchOrThrow from '../util/fetchOrThrow';

// Material-UI Components
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  CardMedia,
  Box,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material"

// Icons
import {
  Close as CloseIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Pending as PendingIcon,
  Replay as ReplayIcon,
  Publish as PublishIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Anchor as AnchorIcon,
} from "@mui/icons-material"
import { makeStyles } from "tss-react/mui"

// Custom Components and Hooks
import { useTranslation } from "./LocalizationProvider"
import PositionValue from "./PositionValue"
import usePositionAttributes from "../attributes/usePositionAttributes"
import { devicesActions, errorsActions } from "../../store"
import { useCatch } from "../../reactHelper"
import { useAttributePreference } from "../util/preferences"
import { useDeviceReadonly, useAdministrator, useManager, useLimitCommand, useRestriction } from "../util/permissions"
import { getStatusColor } from "../../common/util/formatter"

// Dialogs
import RemoveDialog from "./RemoveDialog";
import MultiActionDialog from "./MultiActionDialog"
import AnchorDialog from "./AnchorDialog"
import RemoveAnchorDialog from "./RemoveAnchorDialog"
import LockUnlockConfirmationDialog from "./confirmation-dialog"
import { SuccessToast } from "./success-toast"

const playBlockSound = () => {
  try {
    const audio = new Audio("./resources/bloqueio.wav")
    audio.volume = 0.5
    audio.play().catch(() => {
      const fallbackAudio = new Audio("./src/resources/bloqueio.wav")
      fallbackAudio.volume = 0.5
      fallbackAudio.play().catch(console.warn)
    })
  } catch (error) {
    console.warn("Erro no áudio de bloqueio:", error)
  }
}

const playUnblockSound = () => {
  try {
    const audio = new Audio("./resources/desbloqueio.wav")
    audio.volume = 0.5
    audio.play().catch(() => {
      const fallbackAudio = new Audio("./src/resources/desbloqueio.wav")
      fallbackAudio.volume = 0.5
      fallbackAudio.play().catch(console.warn)
    })
  } catch (error) {
    console.warn("Erro no áudio de desbloqueio:", error)
  }
}

const StatusCardRoot = styled("div")(({ theme, desktoppadding }) => ({
  pointerEvents: "none",
  position: "fixed",
  zIndex: 1000, // Reduzido de 5 para evitar conflitos
  left: "50%",
  transform: "translateX(-50%)",
  [theme.breakpoints.up("md")]: {
    left: `calc(50% + ${desktoppadding} / 2)`,
    bottom: theme.spacing(3),
  },
  [theme.breakpoints.down("md")]: {
    left: "50%",
    bottom: `calc(${theme.spacing(3)} + ${theme.dimensions.bottomBarHeight}px)`,
  },
}))

const StatusCardContainer = styled(Card)(({ theme }) => ({
  pointerEvents: "auto",
  width: theme.dimensions.popupMaxWidth,
  backgroundColor: theme.palette.background.paper,
  position: "relative", // Adicionado para contexto de posicionamento
}))

const CardMediaWrapper = styled(CardMedia)(({ theme }) => ({
  cursor: "move",
  height: theme.dimensions.popupImageHeight,
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  position: "relative", // Adicionado para contexto de posicionamento
}))

const CardHeader = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1, 1, 0, 2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "rgba(0,0,0,0.3)",
  zIndex: 1, // Contexto local
}))

const TextContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.3, 0.3),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
  flex: 1, // Para ocupar espaço disponível
}))

// Simplificado - removido overlay invisível desnecessário
const CloseButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(0,0,0,0.3)",
  color: "#FFFFFF",
  minWidth: "44px",
  minHeight: "44px",
  zIndex: 2, // Maior que o header
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.9)",
  },
  // Garantir que seja clicável
  pointerEvents: "auto",
  cursor: "pointer",
}))

const useStyles = makeStyles()((theme, { desktopPadding }) => ({
  card: {
    pointerEvents: "auto",
    width: theme.dimensions.popupMaxWidth,
  },
  media: {
    height: theme.dimensions.popupImageHeight,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
  },
  mediaButton: {
    color: theme.palette.primary.contrastText,
    mixBlendMode: "difference",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 1, 0, 2),
  },
  content: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    maxHeight: theme.dimensions.cardContentMaxHeight,
    overflow: "auto",
  },
  icon: {
    width: "25px",
    height: "25px",
    filter: "brightness(0) invert(1)",
  },
  table: {
    "& .MuiTableCell-sizeSmall": {
      paddingLeft: 0,
      paddingRight: 0,
    },
    "& .MuiTableCell-sizeSmall:first-of-type": {
      paddingRight: theme.spacing(1),
    },
  },
  cell: {
    borderBottom: "none",
  },
  actions: {
    justifyContent: "space-between",
  },
  root: {
    pointerEvents: "none",
    position: "fixed",
    zIndex: 1000,
    left: "50%",
    [theme.breakpoints.up("md")]: {
      left: `calc(50% + ${desktopPadding} / 2)`,
      bottom: theme.spacing(3),
    },
    [theme.breakpoints.down("md")]: {
      left: "50%",
      bottom: `calc(${theme.spacing(3)} + ${theme.dimensions.bottomBarHeight}px)`,
    },
    transform: "translateX(-50%)",
  },
}))

const StatusRow = ({ name, content }) => {
  const { classes } = useStyles({ desktopPadding: 0 })

  return (
    <TableRow>
      <TableCell className={classes.cell}>
        <Typography variant="body2">{name}</Typography>
      </TableCell>
      <TableCell className={classes.cell}>
        <Typography variant="body2" color="textSecondary">
          {content}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

const StatusCard = ({ deviceId, position, onClose, disableActions, desktopPadding = 0 }) => {
  const { classes } = useStyles({ desktopPadding })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const t = useTranslation()

  const device = useSelector((state) => state.devices.items[deviceId])
  const groups = useSelector((state) => state.groups.items)
  const shareDisabled = useSelector((state) => state.session.server.attributes.disableShare)
  const user = useSelector((state) => state.session.user)
  const revenda = useSelector((state) => state.session.revenda)

  const positionItems = useAttributePreference("positionItems", "fixTime,address,speed,totalDistance")
  const navigationAppLink = useAttributePreference("navigationAppLink")
  const navigationAppTitle = useAttributePreference("navigationAppTitle")
  const positionAttributes = usePositionAttributes(t)

  const readonly = useRestriction('readonly');
  const deviceReadonly = useDeviceReadonly()
  const isAdmin = useAdministrator()
  const isManager = useManager()
  const limitCommands = useLimitCommand()

  const [anchorEl, setAnchorEl] = useState(null)
  const [openAnchorDialog, setOpenAnchorDialog] = useState(false)
  const [openRemoveAnchorDialog, setOpenRemoveAnchorDialog] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState("delete")
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastSeverity, setToastSeverity] = useState("success")
  const [toastOpen, setToastOpen] = useState(false)
  const [sended, setSended] = useState(null)
  const [streetView, setStreetView] = useState(false)
  const [activePanel, setActivePanel] = useState(0)
  const [commandLoading, setCommandLoading] = useState(false)
  const [openLockUnlockDialog, setOpenLockUnlockDialog] = useState(false)

  const getBlockedState = () => {
    if (!position?.attributes) return false

    const blockedValue = position.attributes.blocked

    if (typeof blockedValue === "boolean") {
      return blockedValue
    }
    if (typeof blockedValue === "string") {
      return blockedValue.toLowerCase() === "true" || blockedValue === "1"
    }
    if (typeof blockedValue === "number") {
      return blockedValue === 1
    }

    return false
  }

  const isBlocked = getBlockedState()


  const statusKey = getStatusColor(device?.status)
  const hasAnchor = device?.attributes?.hasAnchor
  const deviceImage = device?.attributes?.deviceImage
  const hasValidGroup = device?.groupId && groups[device.groupId]

  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);


  useEffect(() => {
    if (position) {
      fetch(
        `https://street-view.entrack-plataforma.workers.dev/?heading=${position.course}&location=${position.latitude},${position.longitude}&size=288x144&return_error_code=true`,
      ).then((response) => setStreetView(response.ok))
    }
  }, [position])

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = ""
      }
    }
  }, [isMobile])

  const handleDialogResult = useCatch(async (confirmed) => {
    setDialogOpen(false)
    if (confirmed) {
      try {
        const response = await fetch("/api/devices")
        const devices = await response.json()
        dispatch(devicesActions.refresh(devices))

        if (dialogType === "delete") {
          onClose?.()
        }
      } catch (err) {
        setToastMessage("Erro ao executar ação")
        setShowSuccessToast(true)
      }
    }
  })

  const handleLockUnlockClick = () => {
    if (commandLoading) return
    setOpenLockUnlockDialog(true)
  }

  const handleRemove = useCatch(async (confirmed) => {
    if (confirmed) {
      const res = await fetchOrThrow('/api/devices');
      dispatch(devicesActions.refresh(await res.json()));
    }
    setOpenRemoveDialog(false);
  });

  const executeLockUnlock = async () => {
    if (commandLoading) return

    setCommandLoading(true)

    const currentBlockedState = getBlockedState()
    const commandType = currentBlockedState ? "engineResume" : "engineStop"

    if (!position?.deviceId && !deviceId) {
      setToastMessage("Erro: ID do dispositivo não encontrado")
      setToastSeverity("error")
      setToastOpen(true)
      setShowSuccessToast(true)
      setCommandLoading(false)
      return
    }

    try {
      const requestBody = {
        deviceId: position?.deviceId || deviceId,
        type: commandType,
      }

      const response = await fetch("/api/commands/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(requestBody),
      })

      if (response.ok) {
        setSended(response.status)

        if (commandType === "engineStop") {
          setToastMessage(t("deviceBlocked") || "Comando de bloqueio enviado com sucesso!")
          playBlockSound()
        } else {
          setToastMessage(t("deviceUnblocked") || "Comando de desbloqueio enviado com sucesso!")
          playUnblockSound()
        }

        setToastSeverity("success")
        setToastOpen(true)
        setShowSuccessToast(true)

        const refreshAttempts = [1000, 3000, 5000]

        refreshAttempts.forEach((delay) => {
          setTimeout(async () => {
            try {
              const refreshResponse = await fetch("/api/devices")
              if (refreshResponse.ok) {
                const devices = await refreshResponse.json()
                dispatch(devicesActions.refresh(devices))
              }
            } catch (error) {
              console.error("Error refreshing devices:", error)
            }
          }, delay)
        })
      } else {
        const errorText = await response.text()

        var str = String(response.statusText)

        if (str.search(/supported/i) > 0 || errorText.includes("supported")) {
          dispatch(errorsActions.push(t("commandNotSupported")))
          setToastMessage(t("commandNotSupported") || "Comando não suportado pelo dispositivo")
          setToastSeverity("error")
          setToastOpen(true)
          setShowSuccessToast(true)
        } else {
          dispatch(errorsActions.push(response.statusText || errorText))
          setToastMessage(response.statusText || errorText || "Erro ao executar comando")
          setToastSeverity("error")
          setToastOpen(true)
          setShowSuccessToast(true)
        }
      }
    } catch (error) {
      setToastMessage(t("networkError") || "Erro de comunicação")
      setToastSeverity("error")
      setToastOpen(true)
      setShowSuccessToast(true)
    } finally {
      setCommandLoading(false)
    }
  }

  const handleGeofence = useCatch(async () => {
    if (!position) return
    const area = `CIRCLE (${position.latitude} ${position.longitude}, 50)`

    const res = await fetchOrThrow("/api/geofences", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: t("sharedGeofence"), area }),
    })

    const geo = await res.json()

    await fetchOrThrow("/api/permissions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: position.deviceId, geofenceId: geo.id }),
    })

    navigate(`/settings/geofence/${geo.id}`)
  })

  const handleClose = useCallback(
    (event) => {
      // Prevenir propagação para evitar conflitos com drag
      event?.stopPropagation()
      onClose?.()
    },
    [onClose],
  )

  const headerTextStyles = {
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "0.5px",
    textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
  }

  return (
    <StatusCardRoot desktoppadding={desktopPadding}>
      {device && (
        <Rnd
          default={{ x: 0, y: 0, width: "auto", height: "auto" }}
          enableResizing={false}
          dragHandleClassName="draggable-header"
          style={{ position: "relative" }}
          // Evitar conflitos com cliques nos botões
          cancel=".no-drag"
        >
          <StatusCardContainer elevation={3}>
            {activePanel === 0 && (deviceImage || streetView) && (
              <CardMediaWrapper
                className="draggable-header"
                title={t("positionImage")}
                image={
                  deviceImage
                    ? `/api/media/${device?.uniqueId}/${deviceImage}`
                    : `https://street-view.entrack-plataforma.workers.dev/?heading=${position?.course || 0}` +
                      `&location=${position?.latitude},${position?.longitude}&size=288x144&return_error_code=true`
                }
                alt={device?.name || t("eventDeviceUnknown")}
              >
                <CardHeader className="draggable-header">
                  <TextContainer>
                    <Typography component="div" variant="body2" style={headerTextStyles}>
                      {device?.name || t("eventDeviceUnknown")}
                      {device?.category && (
                        <>
                          <br />
                          {t(`category${device.category[0].toUpperCase() + device.category.slice(1)}`)}
                        </>
                      )}
                    </Typography>

                    {hasValidGroup && (
                      <Typography
                        variant="caption"
                        style={{
                          color: "#FFFFFF",
                          display: "block",
                          marginTop: 0.5,
                          textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
                        }}
                      >
                        {groups[device.groupId].name}
                      </Typography>
                    )}
                  </TextContainer>

                  <CloseButton
                    size="small"
                    aria-label={t("sharedClose")}
                    onClick={handleClose}
                    className="no-drag" // Prevenir drag neste elemento
                  >
                    <CloseIcon fontSize="small" />
                  </CloseButton>
                </CardHeader>
              </CardMediaWrapper>
            )}

            {!deviceImage && !streetView && (
              <CardHeader className="draggable-header">
                <TextContainer>
                  <Typography variant="body2" style={headerTextStyles}>
                    {device?.name}
                  </Typography>
                </TextContainer>
                <CloseButton
                  size="small"
                  aria-label={t("sharedClose")}
                  onClick={handleClose}
                  className="no-drag" // Prevenir drag neste elemento
                >
                  <CloseIcon fontSize="small" />
                </CloseButton>
              </CardHeader>
            )}

            {activePanel === 0 && position && (
              <CardContent className={classes.content}>
                <Table size="small" classes={{ root: classes.table }}>
                  <TableBody>
                    {positionItems
                      .split(",")
                      .filter((key) => position.hasOwnProperty(key) || position.attributes.hasOwnProperty(key))
                      .map((key) => (
                        <StatusRow
                          key={key}
                          name={positionAttributes[key]?.name || key}
                          content={
                            <PositionValue
                              position={position}
                              property={position.hasOwnProperty(key) ? key : null}
                              attribute={position.hasOwnProperty(key) ? null : key}
                            />
                          }
                        />
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            )}

            <CardActions className="no-drag">
              <Tooltip title={t("sharedOptions")}>
                <span>
                  <IconButton
                    color="secondary"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    disabled={!position || !device}
                  >
                    <PendingIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t("reportReplay")}>
                <span>
                  <IconButton onClick={() => navigate(`/replay?deviceId=${deviceId}`)} disabled={disableActions || !position || !device}>
                    <ReplayIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {(isAdmin || isManager) && (
                <>
                  <Tooltip title={t("deviceSendCommand")}>
                    <span>
                      <IconButton
                        onClick={() => navigate(`/settings/device/${deviceId}/command`)}
                        disabled={disableActions || !device}
                      >
                        <PublishIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={t("sharedEdit")}>
                    <span>
                      <IconButton
                        onClick={() => navigate(`/settings/device/${deviceId}`)}
                        disabled={disableActions || deviceReadonly || !device}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}

              {limitCommands && !isAdmin && !isManager && (
                <Tooltip
                  title={commandLoading ? "Enviando comando..." : isBlocked ? t("sharedUnlock") : t("sharedLock")}
                >
                  <span>
                    <IconButton
                      color={isBlocked ? "error" : "success"}
                      onClick={handleLockUnlockClick}
                      disabled={disableActions || deviceReadonly || !device || commandLoading}
                      style={{
                        backgroundColor: isBlocked ? "rgba(244, 67, 54, 0.1)" : "rgba(76, 175, 80, 0.1)",
                        opacity: commandLoading ? 0.6 : 1,
                      }}
                    >
                      {commandLoading ? (
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            border: "2px solid #ccc",
                            borderTop: "2px solid #333",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                      ) : isBlocked ? (
                        <LockIcon />
                      ) : (
                        <LockOpenIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {(isAdmin || isManager || limitCommands) && (
                <Tooltip title={hasAnchor ? (device.attributes?.lockOnExit ? "Âncora Ativa (Bloqueia ao sair)" : "Âncora Ativa (Não bloqueia)") : "Sem Âncora Ativa"}>
                  <span>
                    <IconButton
                      color={hasAnchor ? (device.attributes?.lockOnExit ? "error" : "success") : "default"}
                      onClick={() => (hasAnchor ? setOpenRemoveAnchorDialog(true) : setOpenAnchorDialog(true))}
                      disabled={disableActions || deviceReadonly || !device}
                    >
                      <AnchorIcon
                        sx={{
                          color: theme =>
                            !hasAnchor
                              ? theme.palette.neutral.main // Cinza quando não tem âncora
                              : device.attributes?.lockOnExit
                                ? theme.palette.error.main // Vermelho quando bloqueia ao sair
                                : theme.palette.success.main // Verde quando não bloqueia
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

                <Tooltip title={t('sharedRemove')}>
                <span>
                  <IconButton
                    color="error"
                    onClick={() => setOpenRemoveDialog(true)}
                    disabled={disableActions || deviceReadonly || !device}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>

            </CardActions>
          </StatusCardContainer>
        </Rnd>
      )}

      {position && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => navigate(`/position/${position.id}`)}>
            <Typography color="primary">{t("sharedTelemetry")}</Typography>
          </MenuItem>
          {!readonly && <MenuItem onClick={handleGeofence}>{t('sharedCreateGeofence')}</MenuItem>}
          <MenuItem
            component="a"
            href={`https://www.google.com/maps/search/?api=1&query=${position.latitude},${position.longitude}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("linkGoogleMaps")}
          </MenuItem>
          <MenuItem
            component="a"
            href={`http://maps.apple.com/?ll=${position.latitude},${position.longitude}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("linkAppleMaps")}
          </MenuItem>
          <MenuItem
            component="a"
            href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude},${position.longitude}&heading=${position.course}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("linkStreetView")}
          </MenuItem>
          {navigationAppTitle && (
            <MenuItem
              component="a"
              href={navigationAppLink
                .replace("{latitude}", position.latitude)
                .replace("{longitude}", position.longitude)}
              target="_blank"
              rel="noreferrer"
            >
              {navigationAppTitle}
            </MenuItem>
          )}
          {!shareDisabled && !user.temporary && (
            <MenuItem onClick={() => navigate(`/settings/device/${deviceId}/share`)}>
              <Typography color="secondary">{t("deviceShare")}</Typography>
            </MenuItem>
          )}
        </Menu>
      )}

      <MultiActionDialog open={dialogOpen} actionType={dialogType} itemId={deviceId} onResult={handleDialogResult} />

      <AnchorDialog
        open={openAnchorDialog}
        onClose={() => setOpenAnchorDialog(false)}
        deviceName={device?.name || "Desconhecido"}
        position={position}
        device={device}
        dispatch={dispatch}
      />


   <RemoveDialog
        open={openRemoveDialog}
        endpoint="devices"
        itemId={deviceId}
        onResult={handleRemove}
      />




      <RemoveAnchorDialog
        open={openRemoveAnchorDialog}
        onClose={() => setOpenRemoveAnchorDialog(false)}
        deviceName={device?.name || "Desconhecido"}
        device={device}
        position={position}
        dispatch={dispatch}
      />

      <LockUnlockConfirmationDialog
        open={openLockUnlockDialog}
        onClose={() => setOpenLockUnlockDialog(false)}
        onConfirm={executeLockUnlock}
        isBlocked={isBlocked}
        deviceName={device?.name || "Desconhecido"}
        loading={commandLoading}
      />

      <SuccessToast
        open={showSuccessToast}
        message={toastMessage}
        onClose={() => {
          setShowSuccessToast(false)
          setToastOpen(false)
        }}
      />

      {/* Adicionar CSS para animação de loading */}
      <style jsx="true">{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </StatusCardRoot>
  )
}

export default StatusCard